import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { Button, ConfirmationDialog, MultiSelect } from '../../../components/ui';
import { TrashIcon } from '../../../components/ui/Icons';
import { cn } from '../../../lib/utils';
import { API_BASE_URL } from '../../../config/apiConfig';
import { useEntities } from '../../../hooks/useSWR';
import { mutate as mutateSWR } from 'swr';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  const savedUser = localStorage.getItem('user');
  let userId = null;
  if (savedUser) {
    try {
      userId = JSON.parse(savedUser).id?.toString();
    } catch {
      userId = null;
    }
  }
  return {
    'Content-Type': 'application/json',
    ...(userId && { 'user-id': userId }),
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const mapAssignmentRow = (a) => ({
  id: a.id,
  userId: a.userId,
  userLabel:
    a.user?.userDisplayName || a.user?.userEmail || `User #${a.userId}`,
});

/** Assign users who may read the published procedure (My Procedures). QMS edits via /procedures. */
const ProcedureAssignmentsTab = ({ selectedRecord, onAssignmentsSaved }) => {
  const { theme } = useTheme();
  const documentId = selectedRecord?.id;
  const { data: users = [] } = useEntities('/users');
  const { data: userDepartments = [] } = useEntities('/user-department');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState(null);
  const [filterDepartmentId, setFilterDepartmentId] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [removeConfirm, setRemoveConfirm] = useState(null);

  const loadAssignments = useCallback(async () => {
    if (!documentId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/procedure-assignment/by-document/${documentId}`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) throw new Error('Failed to load assignments');
      const data = await res.json();
      setRows((data || []).map(mapAssignmentRow));
    } catch (e) {
      setError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  useEffect(() => {
    setFilterDepartmentId('');
    setSelectedUserIds([]);
    setError(null);
  }, [documentId]);

  const persistAssignments = async (userIds) => {
    const res = await fetch(`${API_BASE_URL}/procedure-assignment/bulk`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        documentId,
        assignments: userIds.map((userId) => ({
          userId,
          interactionType: 'VIEW',
        })),
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to save assignments');
    }
    await loadAssignments();
    if (documentId) {
      await mutateSWR(`/procedure-assignment/by-document/${documentId}`);
    }
    onAssignmentsSaved?.();
  };

  const addViewers = async () => {
    if (!documentId || !selectedUserIds.length) return;

    const idsToAdd = selectedUserIds
      .map((id) => parseInt(id, 10))
      .filter((id) => Number.isFinite(id) && !rows.some((r) => r.userId === id));

    if (idsToAdd.length === 0) {
      setError('Selected users are already assigned');
      return;
    }

    setAdding(true);
    setError(null);
    try {
      const merged = [...new Set([...rows.map((r) => r.userId), ...idsToAdd])];
      await persistAssignments(merged);
      setSelectedUserIds([]);
    } catch (e) {
      setError(e.message);
    } finally {
      setAdding(false);
    }
  };

  const openRemoveConfirm = (row) => {
    setRemoveConfirm({ id: row.id, userId: row.userId, userLabel: row.userLabel });
  };

  const closeRemoveConfirm = () => {
    if (!removing) setRemoveConfirm(null);
  };

  const confirmRemoveRow = async () => {
    if (!removeConfirm || !documentId) return;

    setRemoving(true);
    setError(null);
    try {
      if (removeConfirm.id) {
        const res = await fetch(
          `${API_BASE_URL}/procedure-assignment/${removeConfirm.id}`,
          { method: 'DELETE', headers: getAuthHeaders() }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.message || 'Failed to remove viewer');
        }
      } else {
        const nextIds = rows
          .filter((r) => r.userId !== removeConfirm.userId)
          .map((r) => r.userId);
        await persistAssignments(nextIds);
      }
      setRemoveConfirm(null);
      await loadAssignments();
      if (documentId) {
        await mutateSWR(`/procedure-assignment/by-document/${documentId}`);
      }
      onAssignmentsSaved?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setRemoving(false);
    }
  };

  const assignedUserIds = new Set(rows.map((r) => r.userId));
  const busy = adding || removing;
  const selectedDepartmentId = filterDepartmentId
    ? parseInt(filterDepartmentId, 10)
    : null;
  const filteredUsers = users.filter((u) => {
    if (u.userIsActive === false || assignedUserIds.has(u.id)) return false;
    if (!selectedDepartmentId) return false;
    return u.departmentId === selectedDepartmentId;
  });
  const userOptions = filteredUsers.map((u) => ({
    value: u.id,
    label: `${u.userDisplayName || u.userEmail} (${u.userEmail})`,
  }));
  const departmentOptions = (userDepartments || [])
    .filter((d) => d.isActive !== false)
    .slice()
    .sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), undefined, {
        sensitivity: 'base',
      })
    );
  const userSelectDisabled = busy || !filterDepartmentId;
  const userPlaceholder = !filterDepartmentId
    ? 'Select department first…'
    : filteredUsers.length === 0
      ? 'No users in this department…'
      : 'Select users…';

  if (!documentId) {
    return (
      <p className={`text-sm ${theme.text.secondary} p-4`}>
        Select a procedure to assign viewers.
      </p>
    );
  }

  return (
    <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(100vh-12rem)]">
      <p className={`text-sm ${theme.text.secondary}`}>
        Assign users who can open this procedure under <strong>My Procedures</strong> (read-only,
        current published revision). Creating and editing procedures is done on the{' '}
        <strong>Procedures</strong> page by QMS staff — not via assignments.
      </p>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 p-2 rounded">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[180px]">
          <label className={`block text-xs mb-1 ${theme.text.secondary}`}>
            Department
          </label>
          <select
            className={`w-full rounded border px-2 py-1.5 text-sm ${theme.border.light} ${theme.colors.surface}`}
            value={filterDepartmentId}
            onChange={(e) => {
              setFilterDepartmentId(e.target.value);
              setSelectedUserIds([]);
              setError(null);
            }}
            disabled={busy}
          >
            <option value="">Select department…</option>
            {departmentOptions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-[2] min-w-[240px]">
          <label className={`block text-xs mb-1 ${theme.text.secondary}`}>
            Users (VIEW access)
          </label>
          <div
            className={cn(userSelectDisabled && 'opacity-50 pointer-events-none')}
            aria-disabled={userSelectDisabled}
          >
            <MultiSelect
              options={userOptions}
              value={selectedUserIds}
              onChange={setSelectedUserIds}
              placeholder={userPlaceholder}
              maxDisplay={2}
            />
          </div>
          {selectedUserIds.length > 0 && (
            <div className={`mt-1 text-xs ${theme.text.secondary}`}>
              {selectedUserIds.length} user{selectedUserIds.length === 1 ? '' : 's'} selected
            </div>
          )}
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={addViewers}
          disabled={!selectedUserIds.length || busy}
        >
          {adding
            ? 'Adding…'
            : selectedUserIds.length > 1
              ? `Add ${selectedUserIds.length} viewers`
              : 'Add viewer'}
        </Button>
      </div>

      {loading ? (
        <p className={`text-sm ${theme.text.secondary}`}>Loading…</p>
      ) : (
        <table className={`w-full text-sm border ${theme.border.light}`}>
          <thead>
            <tr className={theme.colors.background}>
              <th className="text-left p-2">User</th>
              <th className="text-left p-2">Access</th>
              <th className="p-2 w-12" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={3} className={`p-4 text-center ${theme.text.secondary}`}>
                  No viewers assigned yet.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.userId} className={`border-t ${theme.border.light}`}>
                  <td className="p-2">{r.userLabel}</td>
                  <td className={`p-2 text-xs ${theme.text.secondary}`}>VIEW</td>
                  <td className="p-2">
                    <button
                      type="button"
                      onClick={() => openRemoveConfirm(r)}
                      disabled={busy}
                      className={cn(
                        'relative overflow-hidden transition-all duration-200 backdrop-blur border border-transparent',
                        theme.colors.danger,
                        theme.text.inverse,
                        'hover:scale-105 hover:shadow-lg rounded p-1',
                        busy && 'opacity-50 cursor-not-allowed'
                      )}
                      title="Remove viewer"
                      aria-label={`Remove viewer ${r.userLabel}`}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      <ConfirmationDialog
        isOpen={!!removeConfirm}
        onClose={closeRemoveConfirm}
        onConfirm={confirmRemoveRow}
        title="Remove viewer"
        message={
          removeConfirm
            ? `Remove ${removeConfirm.userLabel} from the viewers list?`
            : 'Remove this viewer from the list?'
        }
        warningMessage="The user will no longer see this procedure in My Procedures."
        confirmText="Remove"
        cancelText="Cancel"
        confirmVariant="destructive"
        isLoading={removing}
      />
    </div>
  );
};

export default ProcedureAssignmentsTab;
